import {Button, ButtonIcon, ButtonMenu, FileUploadWithModal, UploadHistoryTableModal} from '@/components-v2'
import {MAIN_COLUMNS} from '@/containers/Fulfillment/InventoryList/InventoryListConstants.jsx'
import useUploadHistory from '@/hooks/useUploadHistory'
import {UPLOAD_HISTORY_TYPE} from '@/utils/constants/globalConstants'
import {useMemo, useState} from 'react'

/**
 * Custom toolbar header rendered above the v2 Inventory List DataTable.
 * Owns the upload/download actions menu and its related modals.
 * @param {{
 *   resetFilterButtonOptions?: {onFiltersReset?: () => void, text?: string},
 *   canDownload: boolean,
 *   isDownloading: boolean,
 *   onDownloadClick: () => void,
 *   isSelectAll: boolean,
 *   rowSelected: any[],
 *   downloadInventoryTemplate: () => void,
 * }} props
 */
export const InventoryListHeader = ({
  resetFilterButtonOptions,
  canDownload,
  isDownloading,
  onDownloadClick,
  isSelectAll,
  rowSelected,
  downloadInventoryTemplate,
}) => {
  const [openUploadHistory, setOpenUploadHistory] = useState(false)
  // ButtonMenu is a single-select control - remount it after each pick so its internal
  // "selected" state (and the checkmark it renders) doesn't stick to the last clicked action.
  const [actionsMenuKey, setActionsMenuKey] = useState(0)
  const [inventoryUpload, toggleInventoryUpload] = useState(false)

  // const {uploadHistoryData, downloadUploadError, downloadPromotionFile, getPromotionFileUploadErrors} =
  //   usePromotionUploadHistory(openUploadHistory)

  const {uploadHistoryData, downloadUploadError, fetchUploadErrors} = useUploadHistory(
    UPLOAD_HISTORY_TYPE?.INVENTORY,
    openUploadHistory
  )

  const uploadHistoryColumns = MAIN_COLUMNS(downloadUploadError)

  // Determine if any sub row has locationId
  const allRows = uploadHistoryData?.data?.rows || uploadHistoryData?.data || []
  const hasLocationId = allRows.some(row => !!row.locationId)

  const dynamicSubColumns = useMemo(
    () => [
      {columnKey: 'productId', header: 'Product ID'},
      ...(!hasLocationId ? [{columnKey: 'warehouseId', header: 'Warehouse ID'}] : []),
      {columnKey: 'errorMsg', header: 'STATUS (REMARK)'},
    ],
    [hasLocationId]
  )

  const actionsMenuItems = useMemo(
    () => [
      {
        id: 'upload',
        label: 'Add Template',
        onClick: () => {
          // Use selectedRows from CustomDataTable if available, otherwise fall back to rowSelecteds
          toggleInventoryUpload(true)
        },
      },
      {
        id: 'upload_history',
        label: 'View Upload History',
        onClick: () => setOpenUploadHistory(true),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [toggleInventoryUpload, setOpenUploadHistory, isSelectAll, rowSelected]
  )

  // ButtonMenu only reports the picked option via onChange - it never calls the option's own onClick.
  const handleActionsMenuChange = value => {
    const selectedAction = actionsMenuItems.find(action => action.id === value?.id)

    if (!selectedAction || selectedAction.disabled) {
      setActionsMenuKey(prevKey => prevKey + 1)
      return
    }

    selectedAction.onClick?.()
    setActionsMenuKey(prevKey => prevKey + 1)
  }

  return (
    <>
      <div className="datatable-custom-header">
        <Button
          variant="tertiary"
          size="medium"
          text={resetFilterButtonOptions?.text ?? 'Reset All'}
          onClick={resetFilterButtonOptions?.onFiltersReset}
        />
        <div className="flex gap-2">
          <ButtonIcon
            icon="download"
            type="default"
            label="Download"
            disabled={!canDownload || isDownloading}
            onClick={onDownloadClick}
          />
          <ButtonMenu
            key={actionsMenuKey}
            options={actionsMenuItems}
            onChange={handleActionsMenuChange}
            height={actionsMenuItems.length * 44 + 16}
            width="280px"
            buttonProps={{
              variant: 'tertiary',
              size: 'medium',
              iconLeading: 'more_vert',
              title: 'More actions',
              'aria-label': 'More actions',
            }}
            menuPosition="bottom-left"
          />
        </div>
      </div>

      <UploadHistoryTableModal
        openUploadHistory={openUploadHistory}
        setOpenUploadHistory={setOpenUploadHistory}
        mainData={uploadHistoryData?.data || []}
        totalRecords={uploadHistoryData?.totalRecords}
        mainColumns={uploadHistoryColumns}
        subColumns={dynamicSubColumns}
        fetchSubRows={fetchUploadErrors}
        isDark={true}
        data-testid="upload-history-modal"
      />
      <FileUploadWithModal
        key={`upload-inventory-${inventoryUpload}`}
        openUpload={inventoryUpload}
        setOpenUpload={toggleInventoryUpload}
        title="Upload Inventory Template"
        uploadFor="inventory"
        uploadKind="template"
        label={'Upload Inventory file'}
        toastMessage={'File has successfully been uploaded and the status can be checked in Upload History'}
        onClickTertiaryButton={downloadInventoryTemplate}
        tertiaryButtonText={'Download template'}
      />
    </>
  )
}
